import React, { useState, useEffect, useCallback } from "react";
import {
  FaBox,
  FaFilter,
  FaTimes,
  FaPlus,
  FaDownload,
  FaChevronDown,
  FaFileAlt,
  FaListUl,
  FaSyncAlt,
} from "react-icons/fa";
import * as clientService from "../../services/client.service";
import * as clientCatalogService from "../../services/clientCatalog.service";
import { productService } from "../../services/product.service";
import "./ClientCatalog.css";

const ClientCatalog = () => {
  // Client Selection
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Client Catalog
  const [clientCatalogProducts, setClientCatalogProducts] = useState([]);

  // Import Modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importableProducts, setImportableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [importFilters, setImportFilters] = useState({
    category: [],
    productName: [],
    finish: [],
    color: [],
    suitableFor: [],
    material: [],
    collection: [],
    packType: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    category: [],
    productName: [],
    finish: [],
    color: [],
    suitableFor: [],
    material: [],
    collection: [],
    packType: [],
  });

  // Filter visibility in modal
  const [showFiltersInModal, setShowFiltersInModal] = useState(true);

  // Load clients on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const clientList = await clientService.getClientList();
        setClients(clientList);
      } catch (error) {
        console.error('Error loading clients:', error);
      }
    };
    loadClients();
  }, []);

  // Load selected client's catalog
  useEffect(() => {
    if (selectedClientId) {
      const client = clients.find(
        (c) => String(c.id) === String(selectedClientId),
      );
      setSelectedClient(client);
      if (client) {
        loadClientCatalog();
      }
    }
  }, [selectedClientId, clients]);

  // Load client catalog from API
  const loadClientCatalog = async () => {
    try {
      // Get the client's clientId from the found client or selected ID
      const clientCode = selectedClient?.clientId || 
                        clients.find(c => String(c.id) === String(selectedClientId))?.clientId;
      
      if (!clientCode) {
        console.error('Client code not found');
        return;
      }
      
      const catalog = await clientCatalogService.getClientCatalog(clientCode);
      setClientCatalogProducts(catalog);
    } catch (error) {
      console.error('Error loading catalog:', error);
      alert('Failed to load client catalog');
    }
  };

  // Load filter options from main catalog
  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await productService.getFilterOptions();
      setFilterOptions(options);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  }, []);

  // Load importable products
  // Load importable products - will be called by useEffect when modal opens or filters change
  // (Defined inline in useEffect to avoid dependency issues)

  // Re-filter products when search term or filters change
  useEffect(() => {
    if (!showImportModal) return;
    
    const loadProducts = async () => {
      try {
        const apiFilters = {
          search: searchTerm,
          ...importFilters,
        };
        const allProducts = await productService.getCatalogProducts({
          ...apiFilters,
          limit: 1000,
        });
        const paginatedProducts = allProducts.data || [];
        const importedUIDs = new Set(clientCatalogProducts.map((p) => p.uid));
        const filtered = paginatedProducts.filter((product) => {
          return !importedUIDs.has(product.uid);
        });
        setImportableProducts(filtered);
      } catch (error) {
        console.error("Error loading importable products:", error);
      }
    };
    
    loadProducts();
  }, [showImportModal, searchTerm, importFilters, clientCatalogProducts]);

  // Open import modal
  const handleOpenImport = () => {
    if (!selectedClientId) {
      alert("Please select a client first");
      return;
    }
    loadFilterOptions();
    setShowImportModal(true);
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Handle import
  const handleImport = async () => {
    if (selectedProducts.size === 0) {
      alert("Please select at least one product to import");
      return;
    }

    try {
      const productIds = Array.from(selectedProducts);
      
      // Get the client's clientId
      const clientCode = selectedClient?.clientId || 
                        clients.find(c => String(c._id || c.id) === String(selectedClientId))?.clientId;
      
      if (!clientCode) {
        alert('Client code not found');
        return;
      }

      // Call API to add products to client catalog
      await clientCatalogService.addProductsToClientCatalog(clientCode, productIds);

      // Reload catalog
      await loadClientCatalog();
      
      setShowImportModal(false);
      setSelectedProducts(new Set());
      setSearchTerm("");
      setImportFilters({
        category: [],
        productName: [],
        finish: [],
        color: [],
        suitableFor: [],
        material: [],
        collection: [],
        packType: [],
      });

      alert(
        `Successfully imported ${selectedProducts.size} product(s) to client catalog!`,
      );
    } catch (error) {
      console.error("Error importing products:", error);
      alert("Error importing products. Please try again.");
    }
  };

  // Handle filter change in modal
  const handleFilterChange = (filterName, value) => {
    setImportFilters((prev) => {
      const currentValues = prev[filterName] || [];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [filterName]: currentValues.filter((v) => v !== value),
        };
      } else {
        return {
          ...prev,
          [filterName]: [...currentValues, value],
        };
      }
    });
  };

  // Clear all filters
  const clearImportFilters = () => {
    setImportFilters({
      category: [],
      productName: [],
      finish: [],
      color: [],
      suitableFor: [],
      material: [],
      collection: [],
      packType: [],
    });
    setSearchTerm("");
  };

  // Handle Tear Sheet
  const handleTearSheet = (product, e) => {
    e.stopPropagation();
    console.log("Tear sheet for product:", product.itemNo);
    // TODO: Implement tear sheet functionality
    alert(`Tear sheet feature for ${product.itemNo}`);
  };

  // Handle BOM (Bill of Materials)
  const handleBOM = (product, e) => {
    e.stopPropagation();
    console.log("BOM for product:", product.itemNo);
    // TODO: Implement BOM functionality
    alert(`BOM feature for ${product.itemNo}`);
  };

  // Handle Product Cycle
  const handleProductCycle = (product, e) => {
    e.stopPropagation();
    console.log("Product Cycle for product:", product.itemNo);
    // TODO: Implement product cycle functionality
    alert(`Product Cycle feature for ${product.itemNo}`);
  };

  // Remove product from client catalog
  const handleRemoveFromCatalog = async (productId) => {
    try {
      // Get the client's clientId
      const clientCode = selectedClient?.clientId || 
                        clients.find(c => String(c._id || c.id) === String(selectedClientId))?.clientId;
      
      if (!clientCode) {
        alert('Client code not found');
        return;
      }

      await clientCatalogService.removeProductFromClientCatalog(clientCode, productId);
      
      // Reload catalog
      await loadClientCatalog();
      alert('Product removed from catalog');
    } catch (error) {
      console.error('Error removing product:', error);
      alert('Failed to remove product from catalog');
    }
  };

  const activeFilterCount = Object.values(importFilters).reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  const renderFilterSection = (title, filterKey, options) => {
    return (
      <div className="filter-section">
        <h6 className="filter-title">{title}</h6>
        <div className="filter-options">
          {(options || []).length === 0 ? (
            <p className="text-muted small">No options available</p>
          ) : (
            (options || []).map((option) => (
              <label key={option.id} className="filter-checkbox">
                <input
                  type="checkbox"
                  checked={
                    importFilters[filterKey]?.includes(option.id) || false
                  }
                  onChange={() => handleFilterChange(filterKey, option.id)}
                />
                <span>{option.name}</span>
              </label>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="client-catalog-container">
      {/* Header */}
      <div className="client-catalog-header">
        <div className="header-content">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="header-icon">
              <FaBox size={32} />
            </div>
            <div>
              <h1 className="mb-0">Client Catalogue</h1>
              <p className="mb-0 text-muted">
                Manage product catalogs for each client
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="client-catalog-main">
        {/* Client Dropdown */}
        <div className="client-selector-bar justify-content-between align-items-center d-flex">
          <div className="d-flex gap-3 align-items-center">
            <div className="client-dropdown-wrapper">
              <button
                className="client-dropdown-btn"
                onClick={() => setShowClientDropdown(!showClientDropdown)}
              >
                <span>
                  {selectedClient?.name ||
                    selectedClient?.clientId ||
                    "Select Client"}
                </span>
                <FaChevronDown
                  className={`dropdown-icon ${showClientDropdown ? "open" : ""}`}
                />
              </button>
              {showClientDropdown && (
                <div className="client-dropdown-list">
                  {clients.length === 0 ? (
                    <div className="dropdown-empty">
                      <p className="text-muted small mb-2">
                        No clients added yet
                      </p>
                      <a
                        href="/dashboard/crm-master/add-client"
                        className="btn btn-sm btn-outline-primary"
                      >
                        <FaPlus className="me-1" /> Add Client
                      </a>
                    </div>
                  ) : (
                    clients.map((client) => (
                      <div
                        key={client._id}
                        className={`dropdown-item ${String(selectedClientId) === String(client.id) ? "active" : ""}`}
                        onClick={() => {
                          setSelectedClientId(String(client.id));
                          setShowClientDropdown(false);
                        }}
                      >
                        <div className="dropdown-item-name">
                          {client.name || client.clientId}
                        </div>
                        <div className="dropdown-item-code">
                          {client.clientId}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <p className="text-muted small mb-0">
              {clientCatalogProducts.length} product
              {clientCatalogProducts.length !== 1 ? "s" : ""} in catalog
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleOpenImport}>
            <FaDownload className="me-2" /> Import Products
          </button>
        </div>

        {/* Catalog Content Area */}
        <section className="catalog-content-area-full">
          {!selectedClientId ? (
            <div className="empty-state">
              <FaBox size={48} className="text-muted mb-3" />
              <h5>Select a Client</h5>
              <p className="text-muted">
                Choose a client from the dropdown to view and manage their
                catalog
              </p>
            </div>
          ) : (
            <>
              {/* Catalog Products Grid */}
              {clientCatalogProducts.length === 0 ? (
                <div className="empty-catalog">
                  <FaBox size={48} className="text-muted mb-3" />
                  <h5>No Products in Catalog</h5>
                  <p className="text-muted">
                    Click "Import Products" to add items to this client's
                    catalog
                  </p>
                </div>
              ) : (
                <div className="products-grid">
                  {clientCatalogProducts.map((product) => (
                    <div key={product.uid} className="product-card">
                      {/* Product Image */}
                      <div className="product-image-container">
                        {product.images && product.images.length > 0 && product.images[0]?.path ? (
                          <img
                            src={product.images[0].path}
                            alt={product.itemNo || 'Product'}
                            className="product-image"
                          />
                        ) : (
                          <div className="product-image-placeholder">
                            <FaBox size={48} className="text-muted" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="product-info">
                        <div className="product-item-no">
                          <strong>Item No:</strong>
                          <span>{product.itemNo || "N/A"}</span>
                        </div>

                        <div className="product-details d-flex justify-content-between align-items-center">
                          <p className="product-category">
                            UID: {product.uid || "N/A"}
                          </p>
                          {product.color && product.color.length > 0 && (
                            <p className="product-specs">
                              <span className="badge bg-secondary">
                                {product.color.length} color
                                {product.color.length > 1 ? "s" : ""}
                              </span>
                            </p>
                          )}
                        </div>

                          {/* Action Buttons */}
                          <div className="product-actions-main">
                            <button
                              className="btn btn-action-icon"
                              onClick={(e) => handleTearSheet(product, e)}
                              title="Tear Sheet"
                            >
                              <FaFileAlt size={14} />
                            </button>
                            <button
                              className="btn btn-action-icon"
                              onClick={(e) => handleBOM(product, e)}
                              title="Bill of Materials"
                            >
                              <FaListUl size={14} />
                            </button>
                            <button
                              className="btn btn-action-icon"
                              onClick={(e) => handleProductCycle(product, e)}
                              title="Product Cycle"
                            >
                              <FaSyncAlt size={14} />
                            </button>
                            <button
                              className="btn btn-action btn-remove ms-auto"
                              onClick={() =>
                                handleRemoveFromCatalog(product.uid)
                              }
                              title="Remove from catalog"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-backdrop">
          <div className="import-modal">
            <div className="modal-header">
              <h5 className="mb-0">Import Products to Client Catalogue</h5>
              <button
                className="btn-close-client"
                onClick={() => {
                  setShowImportModal(false);
                  setSelectedProducts(new Set());
                }}
              />
            </div>

            <div className="modal-body">
              <div className="modal-content-wrapper">
                {/* Modal Sidebar Filters */}
                <aside
                  className={`modal-sidebar ${showFiltersInModal ? "show" : ""}`}
                >
                  <div className="sidebar-header">
                    <h6 className="mb-0">Filters</h6>
                    <button
                      className="btn-close-filters"
                      onClick={() => setShowFiltersInModal(false)}
                    >
                      <FaTimes />
                    </button>
                  </div>

                  {/* Search in filters */}
                  <div className="filter-search mb-3">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Filter Sections */}
                  {renderFilterSection(
                    "Category",
                    "category",
                    filterOptions.category,
                  )}
                  {renderFilterSection(
                    "Product Name",
                    "productName",
                    filterOptions.productName,
                  )}
                  {renderFilterSection(
                    "Finish",
                    "finish",
                    filterOptions.finish,
                  )}
                  {renderFilterSection("Color", "color", filterOptions.color)}
                  {renderFilterSection(
                    "Suitable For",
                    "suitableFor",
                    filterOptions.suitableFor,
                  )}
                  {renderFilterSection(
                    "Material",
                    "material",
                    filterOptions.material,
                  )}
                  {renderFilterSection(
                    "Collection",
                    "collection",
                    filterOptions.collection,
                  )}
                  {renderFilterSection(
                    "Pack Type",
                    "packType",
                    filterOptions.packType,
                  )}

                  {/* Clear Filters */}
                  {activeFilterCount > 0 && (
                    <button
                      className="btn btn-sm btn-outline-danger w-100"
                      onClick={clearImportFilters}
                    >
                      Clear All Filters
                    </button>
                  )}
                </aside>

                {/* Modal Main Content */}
                <section className="modal-main-content">
                  <div className="modal-topbar">
                    <button
                      className="btn-toggle-filters"
                      onClick={() => setShowFiltersInModal(!showFiltersInModal)}
                    >
                      <FaFilter className="me-2" />
                      Filters{" "}
                      {activeFilterCount > 0 && `(${activeFilterCount})`}
                    </button>
                    <div className="selection-info">
                      <span>{selectedProducts.size} selected</span>
                    </div>
                  </div>

                  {/* Products for Import */}
                  <div className="import-products-grid">
                    {importableProducts.length === 0 ? (
                      <div className="no-products">
                        <FaBox size={48} className="text-muted mb-3" />
                        <h6>No products found</h6>
                        <p className="text-muted small">
                          Try adjusting your filters or search term
                        </p>
                      </div>
                    ) : (
                      importableProducts.map((product) => (
                        <div
                          key={product.uid}
                          className={`import-product-card ${selectedProducts.has(product.uid) ? "selected" : ""}`}
                          onClick={() => toggleProductSelection(product.uid)}
                        >
                          <div className="product-image-container">
                            {product.images && product.images.length > 0 && product.images[0]?.path ? (
                              <img
                                src={product.images[0].path}
                                alt={product.itemNo || 'Product'}
                                className="product-image"
                              />
                            ) : (
                              <div className="product-image-placeholder">
                                <FaBox size={32} className="text-muted" />
                              </div>
                            )}
                            <div className="selection-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedProducts.has(product.uid)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleProductSelection(product.uid);
                                }}
                              />
                            </div>
                          </div>

                          <div className="product-info">
                            <div className="product-item-no">
                              {product.item_no || product.itemNo || "N/A"}
                            </div>
                            <p className="product-category text-muted small">
                              UID: {product.uid || "N/A"}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </section>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setSelectedProducts(new Set());
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleImport}
                disabled={selectedProducts.size === 0}
              >
                <FaDownload className="me-2" />
                Import{" "}
                {selectedProducts.size > 0 && `(${selectedProducts.size})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientCatalog;
